import { DATE_FORMATS, formatDate } from '@frontend/mwm-ui'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useState, type ReactElement } from 'react'
import { Handle, Position } from 'reactflow'

import type { AxiosError } from 'axios'
import type { NodeProps } from 'reactflow'
import type { IApiError } from '~/types/api'
import type { ITodolistTask } from '~/types/todolist'
import type { IUserPublic } from '~/types/user'

import TodolistCollapsibleCard from '~/components/TodolistCollapsibleCard'
import { keys as todolistsKeys, useUpdateTodolistTaskStatusMutation } from '~/queries/todolists'
import { useUserDetailsQueries, useUserDetailsQuery } from '~/queries/users'
import { TODOLIST_TASK_STATUS } from '~/types/todolist'
import { displayToast } from '~/utils/toast'

import './styles.scss'

type BasicUserInfo = {
  date: number
  id: string
  job: string
  name: string
  picture?: string
}

const formatUsersQueries = ({ name, picture, user_id }: IUserPublic) => ({
  id: user_id,
  name,
  picture,
})

const TodolistTaskCard = ({ data }: NodeProps<ITodolistTask & { myId: string }>): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(data.status === TODOLIST_TASK_STATUS.IN_PROGRESS)

  /** Queries & Mutations */

  const { mutateAsync: updateTaskStatus } = useUpdateTodolistTaskStatusMutation()

  const teamIds = data.groups.map(({ user_ids }) => user_ids).flat()

  const userQueries = useUserDetailsQueries(teamIds, formatUsersQueries)

  const teamMembers = userQueries.some((e) => e.isLoading)
    ? []
    : userQueries.map(({ data }) => data as BasicUserInfo).filter((value) => value)

  const teams = data.groups.reduce(
    (acc, { name, user_ids }) => {
      const users = user_ids.reduce((acc, id) => {
        const user = teamMembers.find(({ id: teamMemberId }) => teamMemberId === id)
        return user ? [...acc, user] : acc
      }, [] as BasicUserInfo[])

      return [...acc, { name, users }]
    },
    [] as { name: string; users: BasicUserInfo[] }[]
  )

  const formatUser = ({ name, picture }: IUserPublic) => ({
    date: formatDate(data.updated_at, DATE_FORMATS.DEFAULT_WITH_HOURS),
    name,
    picture,
  })

  const { data: updatedBy } = useUserDetailsQuery(data.updated_by, !!data.updated_by, formatUser)

  /** Handlers */

  const handleUpdateTaskStatus = (status: TODOLIST_TASK_STATUS) => async () => {
    try {
      await updateTaskStatus({ status, taskId: data.template_task_id, todolistId: data.todo_list_id })
      await queryClient.invalidateQueries({ queryKey: todolistsKeys.details(data.todo_list_id) })
      displayToast('Task successfully updated', 'success')
    } catch (e) {
      if (isAxiosError(e)) {
        const apiError = e as AxiosError<IApiError>

        if (apiError.message.includes('is not assigned to this task')) {
          displayToast('Task could not be done; you are not assigned to it', 'error')
        } else {
          captureException(e)
          displayToast('Task could not be updated', 'error')
        }
      } else {
        captureException(e)
        displayToast('Task could not be updated', 'error')
      }
    }
  }

  const handleToggle = () => setIsOpen(!isOpen)

  /** Render */

  return (
    <div className="todolist-task-card-component">
      <Handle position={Position.Top} type="target" />
      <TodolistCollapsibleCard
        belongsToTeam={teamIds.includes(data.myId)}
        current={data.status === TODOLIST_TASK_STATUS.IN_PROGRESS}
        description={data.description}
        isDone={data.status === TODOLIST_TASK_STATUS.DONE}
        isOpen={isOpen}
        isSkipped={data.status === TODOLIST_TASK_STATUS.SKIPPED}
        onDone={handleUpdateTaskStatus(TODOLIST_TASK_STATUS.DONE)}
        onSkip={handleUpdateTaskStatus(TODOLIST_TASK_STATUS.SKIPPED)}
        onToggle={handleToggle}
        teams={teams}
        title={data.title}
        updatedBy={updatedBy}
      />
      <Handle id={data.template_task_id} position={Position.Bottom} type="source" />
    </div>
  )
}

export default TodolistTaskCard
